import { Typography } from './Typography';
import { Component, register } from '../Component';
import { theme } from '../theme';
import { IconType, Icon } from './Icon';

interface Props {
  variant: 'info' | 'error' | 'warning';
  title: string;
  required?: boolean;
}

@register('x-info-box')
export class InfoBox extends Component<Props> {
  init() {
    return () => {
      const variant = this.props.variant;

      const getIcon = (): IconType => {
        switch (variant) {
          case 'info':
            return 'info';
          case 'error':
            return 'circle-alert';
          case 'warning':
            return 'triangle-alert';
        }
      };

      const getIconColor = (): string => {
        switch (variant) {
          case 'info':
            return theme.colors.primaryText;
          case 'error':
            return theme.colors.error400;
          case 'warning':
            return theme.colors.warning400;
        }
      };

      return (
        <div class={variant}>
          <div class="icon">
            <Icon icon={getIcon()} style={`color: ${getIconColor()};`} />
          </div>
          <div class="content">
            <Typography variant="label">{this.props.title}</Typography>
            <div>
              <Typography variant="body1">
                <slot></slot>
              </Typography>
            </div>
          </div>
        </div>
      );
    };
  }

  getStyle() {
    return StyledInfoBox;
  }
}

const StyledInfoBox = cssStylesheet(css`
  :host {
    width: 100%;
    display: block;
    /* Slotted content is often a raw error string — a URL or a stack frame with
       no spaces to break on. Inherited, so it reaches the slotted text through
       Typography's shadow root. */
    overflow-wrap: anywhere;
  }

  /* A neutral panel with the variant's colour as a bar down the left edge. */
  :host > div {
    display: flex;
    padding: 0.5rem 0.75rem;
    color: ${theme!.colors.onSurface};
    background: ${theme!.colors.subtle400};
    border-left: 4px solid;
  }

  .info {
    border-left-color: ${theme!.colors.primary400};
  }

  .error {
    border-left-color: ${theme!.colors.error400};
  }

  .warning {
    border-left-color: ${theme!.colors.warning400};
  }

  .icon {
    flex: 0 1 content;
    padding: 0 0.75rem 0 0;
  }

  .content {
    flex: 1;
    /* A flex item's automatic minimum size is its content's intrinsic width, so
       without this a long unbreakable string widens the item past the box and
       spills out rather than wrapping inside it. */
    min-width: 0;
  }
`);
